/**
 * Payment schemas
 *
 * Note: Payment methods are handled by the payments-manager plugin,
 * not as Strapi content types. This file contains schemas for
 * crypto payment configuration and related types.
 */
import { z } from "zod";
import { StrapiIdSchema } from "./common.schemas";
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Billing data input schema (matches pm.billing-data Strapi component)
 */
export const BillingDataInputSchema = z
    .object({
    name: z.string().nullish(),
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postal_code: z.string().nullish(),
    country: z.string().nullish(),
    tax_id: z.string().nullish(),
    contact: z.string().nullish(),
    type: z.enum(["individual", "company"]).nullish(),
})
    .strict();
/**
 * Crypto payment config input schema
 */
export const CryptoPaymentConfigInputSchema = z
    .object({
    isEnabled: z.boolean().optional(),
    walletAddress: z.string().optional(),
    supportedChains: z.array(z.string()).optional(),
    supportedCurrencies: z.array(z.string()).optional(),
    minimumAmount: z.number().positive().optional(),
    notes: z.string().optional(),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Billing data schema (matches pm.billing-data Strapi component)
 *
 * Note: id is optional because billing data is a Strapi component that may not exist.
 * When no billing data exists, the API returns an empty object {}.
 */
export const BillingDataSchema = z.object({
    id: StrapiIdSchema.optional(),
    name: z.string().nullish(),
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postal_code: z.string().nullish(),
    country: z.string().nullish(),
    tax_id: z.string().nullish(),
    contact: z.string().nullish(),
    type: z.enum(["individual", "company"]).nullish(),
});
/**
 * Crypto payment config schema - matches Strapi content type
 */
export const CryptoPaymentConfigSchema = z.object({
    isEnabled: z.boolean(),
    walletAddress: z.string(),
    supportedChains: z.array(z.string()).optional(),
    supportedCurrencies: z.array(z.string()).optional(),
    minimumAmount: z.number().optional(),
    notes: z.string().optional(),
});
/**
 * Crypto payment options schema (for frontend display)
 * Returned by /crypto-payment-config/options endpoint - includes QR code
 */
export const CryptoPaymentOptionsSchema = z.object({
    isEnabled: z.boolean(),
    walletAddress: z.string().optional(),
    supportedChains: z.array(z.string()).optional(),
    supportedCurrencies: z.array(z.string()).optional(),
    qrCodeDataUrl: z.string().nullish(),
    minimumAmount: z.number().optional(),
    notes: z.string().optional(),
});
/**
 * Revolut payment method token schema
 * Note: API returns orderId (not publicId) - used to confirm payment method creation
 */
export const RevolutPaymentMethodTokenSchema = z.object({
    token: z.string(),
    orderId: z.string(),
});
/**
 * Revolut payment confirmation schema
 */
export const RevolutPaymentConfirmationSchema = z.object({
    success: z.boolean(),
    paymentMethodId: z.number().optional(),
    error: z.string().optional(),
});
